import React, { useMemo } from 'react';
import {
  Box,
  ChipsToggler,
  Controller,
  ControllerWrapper,
  FieldWrapper,
  FlatTextInput,
  FormFieldsContainer,
  Hint,
  InfoOutlineIcon,
  Inscription,
  Label,
  NotVisibleICount,
  PayoutRuleType,
  RadioButton,
  SelectDropdown,
  Text,
  useFormContext,
  useWatch,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import { DEFAULT_EMPTY_CHARACTER, REQUIRED_TEXT } from '@/common/constants';
import { Option } from '@/common/types';
import { FeatureFlags } from '@/modules/featureFlags/featureFlags.constants';
import { useFeatureFlag } from '@/modules/featureFlags/hooks/useFeatureFlag';
import theme from '@/styles/theme';

import { useSubmitFormStore } from '../../../../stores';

import {
  CARD_PURSE_LOCK,
  CARD_PURSE_UNLOCK,
  LOCK_CARD_PURSE_OPTIONS,
  PAYOUT_RULE_OPTIONS,
  TierFormField,
  TierFormValues,
} from './tierItem.types';

const StyledInfoIcon = styled(InfoOutlineIcon)`
  cursor: pointer;
`;

interface Props {
  id: string;
  isDisabled?: boolean;
  viewMode?: boolean;
  isPayoutRuleEditable?: boolean;
  isTemplateMode?: boolean;
  isEmbeddedOnly?: boolean;
  isLockCardPurseAvailable?: boolean;
  isLockCardPurseEditable?: boolean;
  linkedPlanOptions?: Option[];
}

const TierFormFields: React.FC<Props> = ({
  id,
  isDisabled = false,
  viewMode = false,
  isPayoutRuleEditable = false,
  isTemplateMode = false,
  isEmbeddedOnly = false,
  isLockCardPurseAvailable = false,
  isLockCardPurseEditable = false,
  linkedPlanOptions = [],
}) => {
  const { control } = useFormContext<TierFormValues>();

  const isSendTriggerNotification = useWatch({ control, name: TierFormField.isSendTriggerNotification });
  const displayName = useWatch({ control, name: TierFormField.displayName });
  const planName = useWatch({ control, name: TierFormField.planName });
  const lockCardPurse = useWatch({ control, name: TierFormField.lockCardPurse });
  const planNamesForCardPurseLock = useWatch({ control, name: TierFormField.planNamesForCardPurseLock });
  const aggregateTriggerName = useWatch({ control, name: TierFormField.aggregateTriggerName });
  const embeddedTriggerName = useWatch({ control, name: TierFormField.embeddedTriggerName });
  const isHidePurseLockEnabled = useFeatureFlag(FeatureFlags.HIDE_PURSE_LOCK);

  // Show the lock card purse plans validation only after Save/Activate is clicked,
  // consistent with the rest of the health plan form.
  const saveValidate = useSubmitFormStore((state) => state.saveValidate);
  const activateValidate = useSubmitFormStore((state) => state.activateValidate);
  const showValidation = saveValidate || activateValidate;

  const lockCardPurseOptions = useMemo(
    () => (planNamesForCardPurseLock || []).map((name) => ({ key: name, value: name })),
    [planNamesForCardPurseLock]
  );

  const lockCardPurseLabel = useMemo(
    () => LOCK_CARD_PURSE_OPTIONS.find((opt) => opt.value === lockCardPurse)?.label || lockCardPurse,
    [lockCardPurse]
  );

  const conditionalFields = useMemo(() => {
    const fields: { name: TierFormField; label: string }[] = [];

    if (isSendTriggerNotification) {
      fields.push({ name: TierFormField.isSendTriggerNotification, label: 'Trigger Notification' });
    }
    if (displayName) {
      fields.push({ name: TierFormField.displayName, label: 'Display name' });
    }
    if (planName) {
      fields.push({ name: TierFormField.planName, label: 'Plan' });
    }

    return fields;
  }, [isSendTriggerNotification, displayName, planName]);

  const renderViewText = (value: string | undefined, testId: string) => (
    <Text size="medium" weight="normal" data-testid={testId}>
      {value || DEFAULT_EMPTY_CHARACTER}
    </Text>
  );

  return (
    <FormFieldsContainer
      data-testid={`tier-item_${id}`}
      margin={{ top: 'spacing16' }}
      fieldsTopMargin={theme.spacings.spacing8}
    >
      <FieldWrapper>
        <Label width="310px" name={TierFormField.name}>
          Name
        </Label>
        <ControllerWrapper style={{ flex: 'none', width: '320px' }}>
          <Controller
            name={TierFormField.name}
            control={control}
            render={({ field }) => {
              if (viewMode) return renderViewText(field.value, `${id}-name-text`);
              return (
                <FlatTextInput
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Enter name"
                  disabled={isDisabled}
                  data-testid={`${id}-name-input`}
                />
              );
            }}
          />
        </ControllerWrapper>
      </FieldWrapper>

      <FieldWrapper>
        <Label width="310px" name={TierFormField.payoutDefinition}>
          Payout definition
        </Label>
        <ControllerWrapper style={{ flex: 'none', width: '320px' }}>
          <Controller
            name={TierFormField.payoutDefinition}
            control={control}
            render={({ field }) => {
              if (viewMode) return renderViewText(field.value, `${id}-payout-definition-text`);
              return (
                <FlatTextInput
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Select payout definition"
                  disabled={isDisabled}
                  data-testid={`${id}-payout-definition-input`}
                />
              );
            }}
          />
        </ControllerWrapper>
      </FieldWrapper>

      {!isTemplateMode && (
        <FieldWrapper>
          <Label width="310px" name={TierFormField.payoutRuleType}>
            Payout rules
          </Label>
          <ControllerWrapper style={{ flex: 1 }}>
            <Controller
              name={TierFormField.payoutRuleType}
              control={control}
              render={({ field }) => {
                if (!isPayoutRuleEditable) {
                  const option = PAYOUT_RULE_OPTIONS.find((opt) => opt.key === field.value);
                  return (
                    <Box direction="row" align="center" gap="spacing8">
                      {renderViewText(option?.value || field.value, `${id}-payout-rule-type-text`)}
                      {option?.hintIconText && (
                        <Hint
                          hintAlign="right"
                          hintWidth={220}
                          hintElement={<StyledInfoIcon color="iconSecondary" />}
                          testId={`${id}-payout-rule-hint`}
                        >
                          <Inscription size="12px" weight={500}>
                            {option.hintIconText}
                          </Inscription>
                        </Hint>
                      )}
                    </Box>
                  );
                }
                return (
                  <ChipsToggler
                    testId={`${id}-payout-rule-type`}
                    options={PAYOUT_RULE_OPTIONS}
                    value={field.value || PayoutRuleType.NO_EXCEPTIONS}
                    onChange={(value: string) => field.onChange(value)}
                    buttonStyle={{
                      height: '40px',
                      padding: `${theme.spacings.spacing8} ${theme.spacings.spacing16}`,
                    }}
                  />
                );
              }}
            />
          </ControllerWrapper>
        </FieldWrapper>
      )}

      {aggregateTriggerName && (
        <FieldWrapper>
          <Label width="310px" name={TierFormField.aggregateTriggerName}>
            Aggregate trigger
          </Label>
          <ControllerWrapper style={{ flex: 'none', width: '320px' }}>
            {renderViewText(aggregateTriggerName, `${id}-aggregate-trigger-text`)}
          </ControllerWrapper>
        </FieldWrapper>
      )}

      {isLockCardPurseAvailable ? (
        <FieldWrapper align={isLockCardPurseEditable ? 'start' : 'center'}>
          <Label
            width="310px"
            name={TierFormField.lockCardPurse}
            pad={{ top: isLockCardPurseEditable ? 'spacing6' : '0' }}
          >
            Lock card purse
          </Label>
          <ControllerWrapper
            style={{
              flex: 'none',
              width: '320px',
              marginTop: isLockCardPurseEditable ? theme.spacings.spacing6 : '0',
            }}
          >
            <Controller
              name={TierFormField.lockCardPurse}
              control={control}
              render={({ field: lockField }) => {
                if (!isLockCardPurseEditable) {
                  const option = LOCK_CARD_PURSE_OPTIONS.find((opt) => opt.value === lockField.value);
                  return (
                    <Box direction="row" align="center" gap="spacing12">
                      <Text size="medium" weight="normal" data-testid={`${id}-lock-card-purse-text`}>
                        {option?.label || lockField.value || DEFAULT_EMPTY_CHARACTER}
                      </Text>
                      {planNamesForCardPurseLock?.length ? (
                        <>
                          <Box
                            width="6px"
                            height="6px"
                            round="full"
                            background="border"
                            flex={{ shrink: 0 }}
                          />
                          <Box pad={{ left: 'spacing12' }}>
                            <NotVisibleICount
                              options={lockCardPurseOptions}
                              boxStyle={{ margin: 0 }}
                              hintOnly
                            />
                          </Box>
                        </>
                      ) : null}
                    </Box>
                  );
                }

                const isLocked = (lockField.value || CARD_PURSE_UNLOCK) === CARD_PURSE_LOCK;

                return (
                  <Box gap="spacing12">
                    <RadioButton
                      name={`${id}-${TierFormField.lockCardPurse}`}
                      data-testid={`${id}-lock-card-purse-lock`}
                      label={
                        <Text color="textBody" weight="normal">
                          Lock
                        </Text>
                      }
                      checked={isLocked}
                      disabled={isDisabled}
                      onChange={() => lockField.onChange(CARD_PURSE_LOCK)}
                    />

                    {isLocked && !isTemplateMode && (
                      <Box pad={{ left: 'spacing28' }} width="320px">
                        <Controller
                          name={TierFormField.planIdsForCardPurseLock}
                          control={control}
                          rules={{
                            validate: (value?: string[]) => (value?.length ? true : REQUIRED_TEXT),
                          }}
                          render={({ field }) => {
                            const hasError = showValidation && !field.value?.length;
                            return (
                              <SelectDropdown
                                id={`${id}-lock-card-purse-plans`}
                                testId={`${id}-lock-card-purse-plans`}
                                name={TierFormField.planIdsForCardPurseLock}
                                options={linkedPlanOptions}
                                values={field.value || []}
                                onChangeValues={(values) => field.onChange(values)}
                                placeholder="Select plans"
                                emptyListMessage="No plans have been found"
                                disabled={isDisabled}
                                errorText={hasError ? REQUIRED_TEXT : undefined}
                              />
                            );
                          }}
                        />
                      </Box>
                    )}

                    <RadioButton
                      name={`${id}-${TierFormField.lockCardPurse}`}
                      data-testid={`${id}-lock-card-purse-unlock`}
                      label={
                        <Text color="textBody" weight="normal">
                          Unlock
                        </Text>
                      }
                      checked={!isLocked}
                      disabled={isDisabled}
                      onChange={() => lockField.onChange(CARD_PURSE_UNLOCK)}
                    />
                  </Box>
                );
              }}
            />
          </ControllerWrapper>
        </FieldWrapper>
      ) : (
        !isHidePurseLockEnabled &&
        !isTemplateMode &&
        !isEmbeddedOnly &&
        lockCardPurse && (
          <FieldWrapper>
            <Label width="310px" name={TierFormField.lockCardPurse}>
              Lock card purse
            </Label>
            <ControllerWrapper style={{ flex: 1 }}>
              <Box direction="row" align="center" gap="spacing12">
                <Text size="medium" weight="normal" data-testid={`${id}-lock-card-purse-text`}>
                  {lockCardPurseLabel}
                </Text>
                {planNamesForCardPurseLock?.length ? (
                  <>
                    <Box width="6px" height="6px" round="full" background="border" flex={{ shrink: 0 }} />
                    <Box pad={{ left: 'spacing12' }}>
                      <NotVisibleICount options={lockCardPurseOptions} boxStyle={{ margin: 0 }} hintOnly />
                    </Box>
                  </>
                ) : null}
              </Box>
            </ControllerWrapper>
          </FieldWrapper>
        )
      )}

      {embeddedTriggerName && (
        <FieldWrapper>
          <Label width="310px" name={TierFormField.embeddedTriggerName}>
            Embedded trigger
          </Label>
          <ControllerWrapper style={{ flex: 'none', width: '320px' }}>
            {renderViewText(embeddedTriggerName, `${id}-embedded-trigger-text`)}
          </ControllerWrapper>
        </FieldWrapper>
      )}

      {conditionalFields.map(({ name, label }) => (
        <FieldWrapper key={name}>
          <Label width="310px" name={name}>
            {label}
          </Label>
          <ControllerWrapper style={{ flex: 'none', width: '320px' }}>
            <Controller
              name={name}
              control={control}
              render={({ field }) => {
                if (viewMode) return renderViewText(field.value as string, `${id}-${name}-text`);
                return (
                  <FlatTextInput
                    value={(field.value as string) || ''}
                    onChange={field.onChange}
                    disabled={isDisabled}
                    data-testid={`${id}-${name}-input`}
                  />
                );
              }}
            />
          </ControllerWrapper>
        </FieldWrapper>
      ))}
    </FormFieldsContainer>
  );
};

export default TierFormFields;
